/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePipe, formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { locations, Time } from '@constants';
import {
  CreateOrUpdateOrderItem,
  LeadInformation,
  LeadOrder,
  OrderShipping,
  OrderShippingQuote,
  Quote,
  ShippingPaginationResult,
} from '@interfaces';
import { LeadService, OrderService } from '@services';
import { extractBusinessLines } from '@utils';

@Component({
  selector: 'unicon-quote-dialog',
  templateUrl: './quote-dialog.component.html',
  styleUrl: './quote-dialog.component.scss',
})
export class QuoteDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orderId?: string;
  private _quote?: Quote;
  @Input() dataLead: LeadOrder = {} as LeadOrder;
  @Input() isLoadingSendQuote: boolean = false;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() send: EventEmitter<{ orderId: string; fileTypeNames: string[] }> =
    new EventEmitter<{ orderId: string; fileTypeNames: string[] }>();
  @Output() onQuote: EventEmitter<string> = new EventEmitter<string>();
  @Input() isLoadingQuote?: boolean;
  @ViewChild('quantityInput') quantityInput?: ElementRef<HTMLInputElement>;
  @ViewChild('priceInput') priceInput?: ElementRef<HTMLInputElement>;
  @ViewChild('billingFirstInput')
  billingFirstInput?: ElementRef<HTMLInputElement>;
  @ViewChild('contactFirstInput')
  contactFirstInput?: ElementRef<HTMLInputElement>;
  public form: FormGroup = this.formBuilder.group({
    sectionShipping: this.formBuilder.array([]),
    sectionBilling: this.formBuilder.array([]),
  });
  protected isLoadingPrice: boolean = false;
  protected editingProductId: string | undefined;
  protected editingQuantityProduct: string | undefined;
  protected isLoadingQuantity: boolean = false;
  protected isLoadingDelete: boolean = false;
  protected add: boolean = false;
  protected arr = Array(3);
  protected currentDate = new Date();
  protected quoteQuantityControl = this.formBuilder.control('');
  protected quoteUnitPriceControl = this.formBuilder.control(0);
  protected editBillingDetail: boolean = false;
  protected isLoadingBillingDetail: boolean = false;
  protected editContactDetails: boolean = false;
  protected isLoadingContactDetails: boolean = false;
  protected editDispatchDetails: boolean = false;
  protected isLoadingDispatchDetails: boolean = false;
  protected editAdvisor: boolean = false;
  protected isLoadingAdvisor: boolean = false;
  protected LOCATIONS: any = locations;
  protected departments: string[] = [];
  protected provinces: string[] = [];
  protected districts: string[] = [];
  protected selectedDepartment: string | null = '';
  protected selectedProvince: string | null = '';
  protected selectedDistrict: string | null = '';
  protected showCalendarGrid: boolean = false;
  protected dateCalendarGrid?: string;
  protected calendarOrderId?: string;
  protected errorCalendar?: string;
  protected calendarGridResult!: ShippingPaginationResult;
  protected showCalendarGridModal: boolean = false;
  protected selectedDate: string = '';
  protected startDateTime: string = '';
  protected endDateTime: string = '';
  protected selectedAddress: string = '';
  protected maxLength: number = 20;
  protected errorMessage: boolean = false;
  protected message: string = '';
  protected businessLines: {
    businessLineId: number;
    businessLineName: string;
    startDateTime: string | null;
    endDateTime: string | null;
  }[] = [];
  private options: Intl.DateTimeFormatOptions = { weekday: 'long' };
  private formatDay: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    'es-ES',
    this.options,
  );
  private originalPrice: number | undefined;
  private originalQuantity: string | undefined;
  private originalSectionValues: Record<string, any> = {};

  // Delivery data properties
  protected selectedShippingFreight: number = 0;
  protected selectedUnloadingMethod: number = 0;
  protected shippingFreightOptions: any[] = [];
  protected unloadingMethodOptions: any[] = [];
  protected isLoadingFreightZones: boolean = false;
  protected isLoadingUnloadingMethods: boolean = false;
  protected isLoadingUpdateShipping: boolean = false;
  protected showFreightZoneSelection: boolean = false;
  protected showUnloadingMethodSelection: boolean = false;

  // Calculated properties for performance optimization
  protected hasBaggedOrBeamProductsValue: boolean = false;
  protected shouldShowSectorTariffValue: boolean = false;
  protected shouldShowUnloadingTariffValue: boolean = false;
  protected hasOnlyBaggedProductsValue: boolean = false;
  protected hasOnlyBeamProductsValue: boolean = false;
  protected hasSameBusinessLineIdValue: boolean = false;
  protected shouldShowFreightZoneValue: boolean = false;
  protected shouldShowUnloadingMethodValue: boolean = false;
  protected shouldShowDeliveryDataValue: boolean = false;
  protected areButtonsEnabledValue: boolean = true;

  protected pdfChecked: boolean = false;
  protected imageChecked: boolean = true;

  protected get isSendDisabled(): boolean {
    const noFormatSelected = !this.pdfChecked && !this.imageChecked;
    const isLoading = this.isLoadingSendQuote;
    const buttonsDisabled = !this.areButtonsEnabledValue;

    return noFormatSelected || isLoading || buttonsDisabled;
  }

  @ViewChild('dispatchDepartmentBox')
  dispatchDepartmentBox!: ElementRef<HTMLDivElement>;
  @ViewChild('dispatchProvinceBox')
  dispatchProvinceBox!: ElementRef<HTMLDivElement>;
  @ViewChild('dispatchDistrictBox')
  dispatchDistrictBox!: ElementRef<HTMLDivElement>;
  @ViewChild('addressNameBox') addressNameBox!: ElementRef<HTMLDivElement>;
  @ViewChild('latitudeBox') latitudeBox!: ElementRef<HTMLDivElement>;
  @ViewChild('longitudeBox') longitudeBox!: ElementRef<HTMLDivElement>;

  constructor(
    private formBuilder: FormBuilder,
    private orderService: OrderService,
    private leadService: LeadService,
    private datePipe: DatePipe,
  ) {}

  // Setter/getter for quote input with performance optimization
  @Input()
  set quote(value: Quote | undefined) {
    this._quote = value;
    this.updateDerivedState();
  }

  get quote(): Quote | undefined {
    return this._quote;
  }

  /**
   * Calculates all derived properties from quote items to optimize performance
   * Only called when quote changes, not on every change detection cycle
   */
  private updateDerivedState(): void {
    const items = this._quote?.items ?? [];

    // Calculate hasBaggedOrBeamProducts (businessLineId = 2 o 4)
    this.hasBaggedOrBeamProductsValue =
      items.length > 0 &&
      items.some(
        item => item.businessLineId === 2 || item.businessLineId === 4,
      );

    // Calculate shouldShowSectorTariff (productos con businessLineId de 4 Y embolsados)
    const hasBeamProducts = items.some(item => item.businessLineId === 4);
    const hasBaggedProducts = items.some(item => item.businessLineId === 2);
    this.shouldShowSectorTariffValue = hasBeamProducts || hasBaggedProducts;

    // Calculate shouldShowUnloadingTariff (solo productos embolsados con businessLineId = 2)
    this.shouldShowUnloadingTariffValue =
      items.length > 0 && items.every(item => item.businessLineId === 2);

    // Calculate hasOnlyBaggedProducts
    this.hasOnlyBaggedProductsValue =
      items.length > 0 && items.every(item => item.businessLineId === 2);

    // Calculate hasOnlyBeamProducts
    this.hasOnlyBeamProductsValue =
      items.length > 0 && items.every(item => item.businessLineId === 4);

    // Calculate hasSameBusinessLineId
    const firstBusinessLineId = items[0]?.businessLineId;
    this.hasSameBusinessLineIdValue =
      items.length > 0 &&
      items.every(item => item.businessLineId === firstBusinessLineId);

    // Calculate shouldShowFreightZone
    this.shouldShowFreightZoneValue =
      this.showFreightZoneSelection ||
      (this._quote?.shipping?.requiresFreightZone === true &&
        this.hasSameBusinessLineIdValue &&
        this._quote?.shipping?.freightZoneId === null);

    // Calculate shouldShowUnloadingMethod
    this.shouldShowUnloadingMethodValue =
      this.showUnloadingMethodSelection ||
      (this._quote?.shipping?.requiresUnloadingMethod === true &&
        this.hasSameBusinessLineIdValue &&
        this._quote?.shipping?.unloadingMethodId === null);

    // Calculate shouldShowDeliveryData
    this.shouldShowDeliveryDataValue =
      this.shouldShowFreightZoneValue || this.shouldShowUnloadingMethodValue;

    // Calculate areButtonsEnabled
    if (!this.shouldShowDeliveryDataValue) {
      this.areButtonsEnabledValue = true;
    } else {
      let freightSelected = true;
      let unloadingSelected = true;

      if (this.shouldShowFreightZoneValue) {
        freightSelected = this.selectedShippingFreight !== 0;
      }

      if (this.shouldShowUnloadingMethodValue) {
        unloadingSelected = this.selectedUnloadingMethod !== 0;
      }

      this.areButtonsEnabledValue = freightSelected && unloadingSelected;
    }
  }

  private onEscapeKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;

    const isEditing =
      this.editBillingDetail ||
      this.editContactDetails ||
      this.editDispatchDetails ||
      this.editingProductId ||
      this.editingQuantityProduct;

    if (!isEditing) return;

    event.stopImmediatePropagation();
    event.preventDefault();

    if (this.editBillingDetail) {
      this.onCancelBillingDetail();
    } else if (this.editContactDetails) {
      this.onCancelContactDetails();
    } else if (this.editDispatchDetails) {
      this.onCancelDispatchDetails();
    } else if (this.editingProductId) {
      this.onCancelEditPrice();
    } else if (this.editingQuantityProduct) {
      this.onCancelEditQuantity();
    }
  };

  ngOnInit() {
    document.addEventListener('keydown', this.onEscapeKey, true);
    this.departments = Object.keys(this.LOCATIONS);
    if (this.quote) {
      this.setForms(this.quote);
      this.calculateBusinessLines();
      this.loadDeliveryOptions();
      this.initializeSelectedOptions();
    }
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.onEscapeKey, true);
  }

  protected onSendClick(): void {
    if (!this.orderId) {
      return;
    }

    const fileTypeNames: string[] = [];

    if (this.pdfChecked) {
      fileTypeNames.push('pdf');
    }

    if (this.imageChecked) {
      fileTypeNames.push('png');
    }

    if (fileTypeNames.length === 0) {
      return;
    }

    this.send.emit({ orderId: this.orderId, fileTypeNames });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quote'] && changes['quote'].currentValue) {
      const quote = changes['quote'].currentValue as Quote;

      // Solo actualizar si no hay ediciones activas ni guardados en proceso
      const isEditing =
        this.editBillingDetail ||
        this.editContactDetails ||
        this.editDispatchDetails ||
        this.editAdvisor;

      const isSaving =
        this.isLoadingBillingDetail ||
        this.isLoadingContactDetails ||
        this.isLoadingDispatchDetails ||
        this.isLoadingAdvisor;

      if (!isEditing && !isSaving) {
        this.setForms(quote);
        this.calculateBusinessLines();
        this.loadDeliveryOptions();
        this.initializeSelectedOptions();
      }
    }
  }

  protected setForms(quote: Quote) {
    if (quote) {
      this.form = this.formBuilder.group({
        sectionBilling: this.formBuilder.group({
          documentNumber: new FormControl({
            disabled: true,
            value: quote.leadIdentificationDocumentNumber
              ? quote.leadIdentificationDocumentNumber
              : '-',
          }),
          name: new FormControl({
            disabled: true,
            value: this.getNames(),
          }),
          leadName: new FormControl({
            disabled: true,
            value: this.quote?.leadNames?.trim()
              ? this.quote?.leadNames?.trim()
              : '-',
          }),
          firstName: new FormControl({
            disabled: true,
            value: this.quote?.leadFirstSurname?.trim()
              ? this.quote?.leadFirstSurname?.trim()
              : '-',
          }),
          secondName: new FormControl({
            disabled: true,
            value: this.quote?.leadSecondSurname?.trim()
              ? this.quote?.leadSecondSurname?.trim()
              : '-',
          }),
          address: new FormControl({
            disabled: true,
            value: this.getAddress(),
          }),
          department: new FormControl({
            disabled: true,
            value: this.quote?.leadAddress?.department?.trim()
              ? this.quote?.leadAddress?.department?.trim()
              : '',
          }),
          province: new FormControl({
            disabled: true,
            value: this.quote?.leadAddress?.province?.trim()
              ? this.quote?.leadAddress?.province?.trim()
              : '',
          }),
          district: new FormControl({
            disabled: true,
            value: this.quote?.leadAddress?.district?.trim()
              ? this.quote?.leadAddress?.district?.trim()
              : '',
          }),
          addressBilling: new FormControl({
            disabled: true,
            value: this.quote?.leadAddress?.addressName?.trim()
              ? this.quote?.leadAddress?.addressName?.trim()
              : '',
          }),
          date: new FormControl({
            disabled: true,
            value: this.currentDate,
          }),
        }),
        sectionContact: this.formBuilder.group({
          contactName: new FormControl({
            disabled: true,
            value: this.getContactInformation(),
          }),
          contactPhone: new FormControl({
            disabled: true,
            value: this.getContactPhone(),
          }),
          contactEmail: new FormControl({
            disabled: true,
            value: this.getContactEmail(),
          }),
        }),
        sectionShipping: this.formBuilder.group({
          shippingAddress: new FormControl({
            disabled: true,
            value: this.getShippingAddress(),
          }),
          dispatchDepartment: new FormControl({
            disabled: true,
            value: '',
          }),
          dispatchProvince: new FormControl({
            disabled: true,
            value: '',
          }),
          dispatchDistrict: new FormControl({
            disabled: true,
            value: '',
          }),
          addressName: new FormControl({
            disabled: true,
            value: '',
          }),
          latitude: new FormControl({
            disabled: true,
            value: '',
          }),
          longitude: new FormControl({
            disabled: true,
            value: '',
          }),
        }),
        sectionAgent: this.formBuilder.group({
          agentName: new FormControl({
            disabled: true,
            value: this.getAgentName(),
          }),
          agentPhone: new FormControl({
            disabled: true,
            value: '-',
          }),
          agentEmail: new FormControl({
            disabled: true,
            value: this.getAgentEmail(),
          }),
        }),
      });
    }
  }

  private calculateBusinessLines(): void {
    this.businessLines = extractBusinessLines(this.quote?.items, [
      'shippingStartDateTime',
      'shippingEndDateTime',
    ]).map(line => ({
      businessLineId: line.businessLineId,
      businessLineName: line.businessLineName,
      startDateTime: line.shippingStartDateTime ?? null,
      endDateTime: line.shippingEndDateTime ?? null,
    }));
  }

  protected getFormattedDateForBusinessLine(businessLine: {
    startDateTime: string | null;
    endDateTime: string | null;
  }): string {
    if (!businessLine.startDateTime) {
      return '-';
    }
    return formatDate(businessLine.startDateTime, 'EEEE, dd/MM/yy', 'es');
  }

  protected getFormattedTimeRangeForBusinessLine(businessLine: {
    startDateTime: string | null;
    endDateTime: string | null;
  }): string {
    if (!businessLine.startDateTime || !businessLine.endDateTime) {
      return '-';
    }

    const startFormatted = this.datePipe.transform(
      businessLine.startDateTime,
      'HH:mm',
    );
    const endFormatted = this.datePipe.transform(
      businessLine.endDateTime,
      'HH:mm',
    );

    if (startFormatted && endFormatted) {
      return `${startFormatted} - ${endFormatted}`;
    }

    return '-';
  }

  protected get isPriceUnchanged(): boolean {
    return (
      Number(this.quoteUnitPriceControl.value) === Number(this.originalPrice)
    );
  }

  protected get isQuantityUnchanged(): boolean {
    return (
      Number(this.quoteQuantityControl.value) === Number(this.originalQuantity)
    );
  }

  protected onEditPrice(id: string) {
    const quoteItem = this.quote?.items.find(item => item.id === id);
    if (!quoteItem) {
      return;
    }

    this.originalPrice = quoteItem.unitPrice;
    this.quoteUnitPriceControl = new FormControl(quoteItem.unitPrice);
    this.editingProductId = id;
    setTimeout(() => {
      this.priceInput?.nativeElement.focus();
    });
  }

  protected onEditQuantity(id: string) {
    const quoteItem = this.quote?.items.find(item => item.id === id);
    if (!quoteItem) {
      return;
    }

    this.originalQuantity = quoteItem.quantity;
    this.quoteQuantityControl = new FormControl(quoteItem.quantity);
    this.editingQuantityProduct = id;
    setTimeout(() => {
      this.quantityInput?.nativeElement.focus();
    });
  }

  protected handleEditPrice(id: string, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isPriceUnchanged) return;
    if (
      this.quoteUnitPriceControl.valid &&
      this.quoteUnitPriceControl.value !== null
    ) {
      this.isLoadingPrice = true;
      const newPrice = {
        unitPrice: this.quoteUnitPriceControl.value,
      };
      if (this.quote?.orderId) {
        this.orderService
          .updatePrice(this.quote?.orderId, id, newPrice)
          .subscribe({
            next: () => {
              this.isLoadingPrice = false;
              this.editingProductId = undefined;
              if (this.quote?.orderId) this.onQuote.emit(this.quote?.orderId);
            },
            error: () => {
              this.isLoadingPrice = false;
            },
          });
      }
    }
  }

  protected handleEditQuantity(id: string, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isQuantityUnchanged) return;
    if (
      this.quoteQuantityControl.valid &&
      this.quoteQuantityControl.value !== null
    ) {
      this.isLoadingQuantity = true;
      if (this.dataLead) {
        const productFiltered = this.dataLead?.items.find(
          item => item?.id === id,
        );
        if (!productFiltered) {
          return;
        }

        const newQuantity = {
          businessLineId: productFiltered.businessLineId,
          productId: productFiltered.productId ?? '',
          productVariantId: productFiltered.productVariantId ?? '',
          quantity: this.quoteQuantityControl.value ?? '0',
        };

        if (!this.quote?.orderId) {
          return;
        }

        this.orderService
          .updateOrderItem(this.dataLead?.id, id, newQuantity)
          .subscribe({
            next: () => {
              this.isLoadingQuantity = false;
              this.editingQuantityProduct = undefined;
              if (this.quote?.orderId) this.onQuote.emit(this.quote?.orderId);
            },
            error: () => {
              this.isLoadingQuantity = false;
            },
          });
      }
    }
  }

  protected onDeleteProduct(id: string) {
    this.isLoadingDelete = true;
    if (this.dataLead) {
      this.orderService.deleteOrderItem(this.dataLead?.id, id).subscribe({
        next: () => {
          this.isLoadingDelete = false;
          if (this.quote?.orderId) this.onQuote.emit(this.quote?.orderId);
        },
        error: () => {
          this.isLoadingDelete = false;
        },
      });
    }
  }

  protected addItem(): void {
    this.add = !this.add;
  }

  protected onAddProduct(
    formValue: CreateOrUpdateOrderItem,
    leadOrderId: string,
  ): void {
    this.addItem();
    this.orderService.createOrderItem(leadOrderId, formValue).subscribe({
      next: () => {
        this.isLoadingQuote = true;
        this.onQuote.emit(leadOrderId);
        this.errorMessage = false;
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage = true;
        this.message = err.error.detail;
        setTimeout(() => {
          this.errorMessage = false;
          this.message = '';
        }, Time.FIVE_SECONDS);
      },
    });
  }

  protected getNames() {
    if (this.quote) {
      const names = [
        this.quote.leadNames?.trim(),
        this.quote.leadFirstSurname?.trim(),
        this.quote.leadSecondSurname?.trim(),
      ].filter(name => name);

      return names.length ? names.join(' ') : '-';
    }

    return '-';
  }

  protected getAddress() {
    const leadAddress = this.quote?.leadAddress;
    const shipping = this.quote?.shipping;

    const addressSource =
      leadAddress !== null && leadAddress !== undefined
        ? leadAddress
        : shipping;

    if (addressSource) {
      const parts = [
        addressSource.addressName?.trim(),
        addressSource.district?.trim(),
        addressSource.province?.trim(),
        addressSource.department?.trim(),
      ].filter(part => part);

      return parts.length ? parts.join(', ') : '-';
    }

    return '-';
  }

  protected getContactInformation() {
    const name = this.quote?.shippingContactInformation?.completeName?.trim();

    return name ? name : '-';
  }

  protected getContactPhone() {
    const phone = this.quote?.shippingContactInformation?.phone?.trim();

    return phone ? phone : '-';
  }

  protected getContactEmail() {
    const email = this.quote?.leadEmail?.trim();

    return email ? email : '-';
  }

  protected getShippingAddress() {
    const shipping = this.quote?.shipping;

    if (shipping) {
      const addressParts = [
        shipping.addressName?.trim(),
        shipping.district?.trim(),
        shipping.province?.trim(),
        shipping.department?.trim(),
      ].filter(part => part); // filtra nulos, undefined y vacíos

      return addressParts.length ? addressParts.join(', ') : '-';
    }

    return '-';
  }

  protected getAgentName() {
    const agent = this.quote?.assignedAgent;

    const firstName = agent?.firstName?.trim();
    const lastName = agent?.lastName?.trim();

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    return '-';
  }

  protected getAgentEmail() {
    const email = this.quote?.assignedAgent?.email?.trim();

    return email ? email : '-';
  }

  protected onEnabledField(sectionName: string) {
    const section = this.form.get(sectionName) as FormGroup;
    if (section) {
      Object.keys(section.controls).forEach(field => {
        if (field !== 'date') {
          section.get(field)?.enable();
        }
      });
    }
  }

  protected onDisabledField(sectionName: string) {
    const section = this.form.get(sectionName) as FormGroup;
    if (section) {
      Object.keys(section.controls).forEach(field => {
        section.get(field)?.disable();
      });
    }
  }

  protected getMaxLength(type: string): number {
    return type === 'DNI' ? 8 : type === 'RUC' ? 11 : type === 'PAS' ? 12 : 20;
  }

  protected onEditBillingDetail() {
    this.editBillingDetail = !this.editBillingDetail;
    this.storeOriginalValues('sectionBilling');
    this.onEnabledField('sectionBilling');
    if (this.quote) {
      this.maxLength = this.getMaxLength(
        this.quote?.leadIdentificationDocumentTypeName,
      );
    }
    setTimeout(() => {
      this.billingFirstInput?.nativeElement.focus();
    });
  }

  protected onEditContactDetails() {
    this.editContactDetails = !this.editContactDetails;
    this.storeOriginalValues('sectionContact');
    this.onEnabledField('sectionContact');
    setTimeout(() => {
      this.contactFirstInput?.nativeElement.focus();
    });
  }

  protected onEditDispatchDetails() {
    this.editDispatchDetails = !this.editDispatchDetails;

    this.storeOriginalValues('sectionShipping');
    this.onEnabledField('sectionShipping');
    this.onAddressEdit();
  }

  protected onSaveEdit() {
    this.editBillingDetail = !this.editBillingDetail;
    this.onDisabledField('sectionBilling');
    this.onSaveEdited('sectionBilling');
    this.updateAddressLeadField();
    this.updateNames();
  }

  protected onSaveContactDetails() {
    this.editContactDetails = !this.editContactDetails;
    this.onDisabledField('sectionContact');
    this.onSaveEdited('sectionContact');
  }

  protected onSaveDispatchDetails() {
    const section = this.form.get('sectionShipping');
    this.editDispatchDetails = !this.editDispatchDetails;
    this.onDisabledField('sectionShipping');
    this.onSaveEdited('sectionShipping');
    this.enabledFields();
    this.updateAddressField();
    section?.get('shippingDate')?.setValue(this.selectedDate);
  }

  private storeOriginalValues(sectionName: string) {
    const section = this.form.get(sectionName) as FormGroup;
    if (section) {
      this.originalSectionValues[sectionName] = section.getRawValue();
    }
  }

  private restoreOriginalValues(sectionName: string) {
    const section = this.form.get(sectionName) as FormGroup;
    const original = this.originalSectionValues[sectionName];
    if (section && original) {
      section.patchValue(original);
      delete this.originalSectionValues[sectionName];
    }
  }

  protected onCancelBillingDetail(event?: Event) {
    event?.stopPropagation();
    this.restoreOriginalValues('sectionBilling');
    this.editBillingDetail = false;
    this.onDisabledField('sectionBilling');
  }

  protected onCancelContactDetails(event?: Event) {
    event?.stopPropagation();
    this.restoreOriginalValues('sectionContact');
    this.editContactDetails = false;
    this.onDisabledField('sectionContact');
  }

  protected onCancelDispatchDetails(event?: Event) {
    event?.stopPropagation();
    this.restoreOriginalValues('sectionShipping');
    this.editDispatchDetails = false;
    this.onDisabledField('sectionShipping');
    this.enabledFields();
  }

  protected onCancelEditPrice(event?: Event) {
    event?.stopPropagation();
    this.editingProductId = undefined;
  }

  protected onCancelEditQuantity(event?: Event) {
    event?.stopPropagation();
    this.editingQuantityProduct = undefined;
  }

  protected onSaveEdited(sectionName: string) {
    const section = this.form.get(sectionName) as FormGroup;
    if (section) {
      if (sectionName === 'sectionBilling') {
        this.isLoadingBillingDetail = true;
        const billingInformation = {
          identificationDocumentNumber: section?.get('documentNumber')?.value,
          names: section?.get('leadName')?.value,
          firstSurname: section?.get('firstName')?.value,
          secondSurname: section?.get('secondName')?.value,
          department: section?.get('department')?.value,
          province: section?.get('province')?.value,
          district: section?.get('district')?.value,
          addressName: section?.get('addressBilling')?.value,
        };

        this.updateLeadInformation(billingInformation, true, 'billing');
      }
      if (sectionName === 'sectionContact') {
        this.isLoadingContactDetails = true;
        const contactInformation = {
          contactName: section?.get('contactName')?.value,
          contactPhone: section?.get('contactPhone')?.value,
        };

        const emailLead = {
          email: section?.get('contactEmail')?.value,
        };
        this.updateLeadInformation(emailLead, false); // No refrescar aquí
        this.updateLeadContactInformation(contactInformation, 'contact'); // Refrescar solo aquí
      }
      if (sectionName === 'sectionShipping') {
        this.isLoadingDispatchDetails = true;
        const address = {
          department: section?.get('dispatchDepartment')?.value,
          province: section?.get('dispatchProvince')?.value,
          district: section?.get('dispatchDistrict')?.value,
          addressName: section?.get('addressName')?.value,
        };

        const addressCoordinates = {
          latitude: section?.get('latitude')?.value,
          longitude: section?.get('longitude')?.value,
        };

        const shippingInformation = {
          startDateTime: this.startDateTime,
          endDateTime: this.endDateTime,
        };

        if (this.selectedAddress === 'address') {
          this.updateLeadContactInformation(
            {
              ...shippingInformation,
              ...address,
            },
            'dispatch',
          );
        } else if (this.selectedAddress === 'coordenates') {
          this.updateLeadContactInformation(
            {
              ...shippingInformation,
              ...addressCoordinates,
            },
            'dispatch',
          );
        } else {
          this.updateLeadContactInformation(
            { ...shippingInformation },
            'dispatch',
          );
        }
      }
    }
  }

  protected onSelectDepartment(event: Event) {
    this.selectedAddress = 'address';
    const section = this.form.get('sectionShipping');
    const department = event.target as HTMLInputElement;
    const selectedDepartment = department.value;
    if (selectedDepartment) {
      this.selectedDepartment = selectedDepartment;
      section?.get('dispatchProvince')?.setValue(null);
      section?.get('dispatchDistrict')?.setValue(null);
      this.selectedProvince = null;
      this.selectedDistrict = null;
      this.provinces = selectedDepartment
        ? Object.keys(this.LOCATIONS[selectedDepartment])
        : [];
      this.districts = [];
      section?.get('dispatchProvince')?.enable();
      section?.get('dispatchDistrict')?.disable();
    }
  }

  protected onSelectProvince(event: Event) {
    this.selectedAddress = 'address';
    const section = this.form.get('sectionShipping');
    const province = event.target as HTMLInputElement;
    const selectedProvince = province.value;
    if (this.selectedDepartment && selectedProvince) {
      this.districts = Object.keys(
        this.LOCATIONS[this.selectedDepartment][selectedProvince],
      );
      section?.get('dispatchDistrict')?.enable();
    } else {
      this.districts = [];
      this.selectedDistrict = null;
    }
  }

  protected updateAddressField() {
    const section = this.form.get('sectionShipping');
    if (section) {
      const addressParts = [
        section?.get('addressName')?.value.trim(),
        section?.get('dispatchDistrict')?.value.trim(),
        section?.get('dispatchProvince')?.value.trim(),
        section?.get('dispatchDepartment')?.value.trim(),
      ].filter(part => part);

      const result = addressParts.length ? addressParts.join(', ') : '-';
      section?.get('shippingAddress')?.setValue(result);
    }
  }

  protected onSelectDistrict(event: Event) {
    this.selectedAddress = 'address';
    const district = event.target as HTMLInputElement;
    this.selectedDistrict = district.value;
    this.updateAddressField();
  }

  protected disableCoordinates() {
    const section = this.form.get('sectionShipping');
    if (section instanceof FormGroup) {
      section.get('latitude')?.disable();
      section.get('longitude')?.disable();
    }
  }

  protected disableFields() {
    const section = this.form.get('sectionShipping');
    if (section instanceof FormGroup) {
      section.get('dispatchDepartment')?.disable();
      section.get('dispatchProvince')?.disable();
      section.get('dispatchDistrict')?.disable();
      section.get('addressName')?.disable();
    }
  }

  protected onFocusAddress() {
    this.selectedAddress = 'address';
    this.addressNameBox.nativeElement.classList.add('border-neutrals-900');
    this.disableCoordinates();
  }

  protected onFocusLongitude() {
    this.selectedAddress = 'coordenates';
    this.longitudeBox.nativeElement.classList.add('border-neutrals-900');
    this.disableFields();
  }

  protected onFocusLatitude() {
    this.selectedAddress = 'coordenates';
    this.latitudeBox.nativeElement.classList.add('border-neutrals-900');
    this.disableFields();
  }

  protected enabledFields() {
    const section = this.form.get('sectionShipping');
    if (section instanceof FormGroup) {
      section.get('dispatchDepartment')?.enable();
      section.get('dispatchProvince')?.enable();
      section.get('dispatchDistrict')?.enable();
      section.get('addressName')?.enable();
      section.get('latitude')?.enable();
      section.get('longitude')?.enable();
    }
  }

  protected onFocusDepartment() {
    this.selectedAddress = 'address';
    this.dispatchDepartmentBox.nativeElement.classList.add(
      'border-neutrals-900',
    );
    this.disableCoordinates();
  }

  protected updateAddressLeadField() {
    const section = this.form.get('sectionBilling');
    if (section) {
      const addressParts = [
        section?.get('addressBilling')?.value.trim(),
        section?.get('district')?.value.trim(),
        section?.get('province')?.value.trim(),
        section?.get('department')?.value.trim(),
      ].filter(part => part);

      const result = addressParts.length ? addressParts.join(', ') : '-';

      section?.get('address')?.setValue(result);
    }
  }

  protected updateNames() {
    const section = this.form.get('sectionBilling');
    if (section) {
      const names = [
        section?.get('leadName')?.value.trim(),
        section?.get('firstName')?.value.trim(),
        section?.get('secondName')?.value.trim(),
      ].filter(name => name);

      const result = names.length ? names.join(' ') : '-';

      return section?.get('name')?.setValue(result);
    }

    return '-';
  }

  protected onAddressEdit(): void {
    const section = this.form.get('sectionShipping');
    if (
      this.quote?.shipping &&
      this.quote?.shipping?.province !== null &&
      this.quote?.shipping?.department === null
    ) {
      this.quote.shipping.province = null;
    }
    if (section && this.quote && this.quote.shipping) {
      section
        ?.get('dispatchDepartment')
        ?.setValue(this.quote.shipping.department);
      section?.get('dispatchProvince')?.setValue(this.quote.shipping.province);
      section?.get('dispatchDistrict')?.setValue(this.quote.shipping.district);
      section?.get('addressName')?.setValue(this.quote.shipping.addressName);
      section?.get('latitude')?.setValue(this.quote.shipping.latitude);
      section?.get('longitude')?.setValue(this.quote.shipping.longitude);
      if (this.quote.shipping.department) {
        this.selectedDepartment = this.quote.shipping.department;
      }

      if (this.quote.shipping.department && this.quote.shipping.province) {
        this.provinces = this.quote.shipping.department
          ? Object.keys(this.LOCATIONS[this.quote.shipping.department])
          : [];
        this.selectedProvince = this.quote.shipping.province;
        section.get('dispatchProvince')?.enable();
      }

      if (
        this.quote.shipping.department &&
        this.quote.shipping.province &&
        this.quote.shipping.district
      ) {
        this.districts = Object.keys(
          this.LOCATIONS[this.quote.shipping.department][
            this.quote.shipping.province
          ],
        );
        this.selectedDistrict = this.quote.shipping.district;
        section.get('dispatchDistrict')?.enable();
      }
    }
  }

  protected onShippingResult(orderId: string, request: OrderShipping): void {
    this.orderService.updateOrderShipping(orderId, request).subscribe({
      error: (error: HttpErrorResponse) => {
        this.showCalendarGrid = false;
        this.errorCalendar = error.error.detail;
        setTimeout(() => {
          this.errorCalendar = undefined;
        }, Time.FIVE_SECONDS);
      },
    });
  }

  protected openCalendarGrid(
    calendarOrderId: string,
    date: string,
    data: ShippingPaginationResult,
  ): void {
    const results = (data?.results || []).map(obj => {
      const newArray = obj?.availability.map(item => {
        const dateObj = new Date(item.startDateTime);
        const dayName = this.formatDay.format(dateObj);
        return { ...item, dayName };
      });
      return { ...obj, availability: newArray };
    });
    this.calendarGridResult = { ...data, results };
    this.dateCalendarGrid = date;
    this.calendarOrderId = calendarOrderId;
  }

  protected onCloseCalendarGrid(date: any): void {
    this.showCalendarGridModal = !this.showCalendarGridModal;
    this.dateCalendarGrid = undefined;
    this.calendarOrderId = undefined;
    this.startDateTime = date.startDateTime;
    this.endDateTime = date.endDateTime;
    if (this.dataLead) {
      if (date) {
        const newDateSelected = this.datePipe.transform(
          date.startDateTime,
          'dd-MM-yyyy',
        );
        this.selectedDate = this.getFormattedDate(newDateSelected ?? '');
      }
    }
  }

  protected getFormattedDate(inputDate: string): string {
    const [day, month, year] = inputDate.split('-').map(Number);
    const originalDate = new Date(year, month - 1, day);

    const dias = [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado',
    ];
    const diaSemana = dias[originalDate.getDay()];

    const dia = originalDate.getDate();
    const mes = originalDate.getMonth() + 1;

    return `${diaSemana}, ${dia}/${mes}/${originalDate.getFullYear()}`;
  }

  private updateLeadInformation(
    request: LeadInformation,
    refreshQuote: boolean = true,
    section?: 'billing' | 'contact',
  ): void {
    this.leadService
      .updateLeadInformation(this.dataLead.leadId, request)
      .subscribe({
        next: () => {
          // Desactivar flag de loading
          if (section === 'billing') {
            this.isLoadingBillingDetail = false;
          } else if (section === 'contact') {
            this.isLoadingContactDetails = false;
          }

          // Refrescar la cotización después de actualizar
          if (refreshQuote && this.quote?.orderId) {
            this.onQuote.emit(this.quote.orderId);
          }
        },
        error: () => {
          // Desactivar flag de loading en caso de error
          if (section === 'billing') {
            this.isLoadingBillingDetail = false;
          } else if (section === 'contact') {
            this.isLoadingContactDetails = false;
          }
        },
      });
  }

  private updateLeadContactInformation(
    request: OrderShippingQuote,
    section?: 'contact' | 'dispatch',
  ): void {
    this.orderService.updateOrderShipping(this.dataLead.id, request).subscribe({
      next: () => {
        // Desactivar flag de loading
        if (section === 'contact') {
          this.isLoadingContactDetails = false;
        } else if (section === 'dispatch') {
          this.isLoadingDispatchDetails = false;
        }

        // Refrescar la cotización después de actualizar
        if (this.quote?.orderId) {
          this.onQuote.emit(this.quote.orderId);
        }
      },
      error: () => {
        // Desactivar flag de loading en caso de error
        if (section === 'contact') {
          this.isLoadingContactDetails = false;
        } else if (section === 'dispatch') {
          this.isLoadingDispatchDetails = false;
        }
      },
    });
  }

  /**
   * Obtiene la lista de BusinessLines únicas que tienen items en la cotización
   */
  public getBusinessLines(): {
    businessLineId: number;
    businessLineName: string;
  }[] {
    if (!this.quote?.items || this.quote.items.length === 0) {
      return [];
    }

    const businessLinesMap = new Map<
      number,
      { businessLineId: number; businessLineName: string }
    >();

    this.quote.items.forEach(item => {
      if (!businessLinesMap.has(item.businessLineId)) {
        businessLinesMap.set(item.businessLineId, {
          businessLineId: item.businessLineId,
          businessLineName: item.businessLineName,
        });
      }
    });

    return Array.from(businessLinesMap.values());
  }

  /**
   * Obtiene los items de cotización filtrados por BusinessLine
   */
  public getItemsByBusinessLine(businessLineId: number) {
    if (!this.quote?.items) {
      return [];
    }
    return this.quote.items.filter(
      item => item.businessLineId === businessLineId,
    );
  }

  /**
   * Verifica si hay productos embolsados o viguetas (businessLineId = 2 o 4)
   */
  protected hasBaggedOrBeamProducts(): boolean {
    return this.hasBaggedOrBeamProductsValue;
  }

  /**
   * Verifica si se debe mostrar 'Tarifa de Sector' (productos con businessLineId de 4 Y embolsados)
   */
  protected shouldShowSectorTariff(): boolean {
    return this.shouldShowSectorTariffValue;
  }

  /**
   * Verifica si se debe mostrar 'Tarifa de descarga' (solo productos embolsados con businessLineId = 2)
   */
  protected shouldShowUnloadingTariff(): boolean {
    return this.shouldShowUnloadingTariffValue;
  }

  /**
   * Verifica si solo hay productos embolsados (businessLineId = 2)
   */
  protected hasOnlyBaggedProducts(): boolean {
    return this.hasOnlyBaggedProductsValue;
  }

  /**
   * Verifica si solo hay productos de viguetas (businessLineId = 4)
   */
  protected hasOnlyBeamProducts(): boolean {
    return this.hasOnlyBeamProductsValue;
  }

  /**
   * Verifica si todos los productos tienen el mismo businessLineId
   */
  protected hasSameBusinessLineId(): boolean {
    return this.hasSameBusinessLineIdValue;
  }

  /**
   * Obtiene el businessLineId común de todos los productos
   */
  protected getCommonBusinessLineId(): number | null {
    if (!this.hasSameBusinessLineIdValue) {
      return null;
    }
    return this._quote?.items?.[0]?.businessLineId || null;
  }

  /**
   * Verifica si se debe mostrar la sección de flete de envío
   */
  protected shouldShowFreightZone(): boolean {
    return this.shouldShowFreightZoneValue;
  }

  /**
   * Verifica si se debe mostrar la sección de método de descarga
   */
  protected shouldShowUnloadingMethod(): boolean {
    return this.shouldShowUnloadingMethodValue;
  }

  /**
   * Verifica si se deben mostrar los datos de entrega
   */
  protected shouldShowDeliveryData(): boolean {
    return this.shouldShowDeliveryDataValue;
  }

  /**
   * Verifica si los botones deben estar habilitados
   */
  protected areButtonsEnabled(): boolean {
    return this.areButtonsEnabledValue;
  }

  /**
   * Inicializa las opciones seleccionadas si ya existen valores
   */
  private initializeSelectedOptions(): void {
    if (this.quote?.shipping) {
      // Inicializar selectedShippingFreight si freightZoneId no es null
      if (this.quote.shipping.freightZoneId !== null) {
        this.selectedShippingFreight = this.quote?.shipping?.freightZoneId || 0;
      }

      // Inicializar selectedUnloadingMethod si unloadingMethodId no es null
      if (this.quote.shipping.unloadingMethodId !== null) {
        this.selectedUnloadingMethod =
          this.quote?.shipping?.unloadingMethodId || 0;
      }
    }
  }

  /**
   * Carga las opciones de entrega desde los servicios
   */
  private loadDeliveryOptions(): void {
    if (
      this.shouldShowFreightZone() ||
      this.quote?.shipping.requiresFreightZone
    ) {
      this.loadFreightZones();
    }
    if (
      this.shouldShowUnloadingMethod() ||
      this.quote?.shipping.requiresUnloadingMethod
    ) {
      this.loadUnloadingMethods();
    }
  }

  /**
   * Carga las zonas de flete desde el servicio
   */
  private loadFreightZones(): void {
    const businessLineId = this.getCommonBusinessLineId();
    if (businessLineId === null) {
      return;
    }

    this.isLoadingFreightZones = true;
    this.orderService.getFreightZones(businessLineId).subscribe({
      next: zones => {
        this.shippingFreightOptions = zones;
        this.isLoadingFreightZones = false;
      },
      error: error => {
        console.error('Error loading freight zones:', error);
        this.isLoadingFreightZones = false;
        // Fallback to empty array
        this.shippingFreightOptions = [];
      },
    });
  }

  /**
   * Carga los métodos de descarga desde el servicio
   */
  private loadUnloadingMethods(): void {
    this.isLoadingUnloadingMethods = true;
    this.orderService.getUnloadingMethods().subscribe({
      next: methods => {
        this.unloadingMethodOptions = methods;
        this.isLoadingUnloadingMethods = false;
      },
      error: error => {
        console.error('Error loading unloading methods:', error);
        this.isLoadingUnloadingMethods = false;
        this.unloadingMethodOptions = [];
      },
    });
  }

  /**
   * Maneja la selección del flete de envío
   */
  protected onShippingFreightChange(freightId: number): void {
    if (!this.orderId || this.isLoadingUpdateShipping) {
      return;
    }

    this.selectedShippingFreight = freightId;
    this.updateDerivedState(); // Update calculated properties
    this.isLoadingUpdateShipping = true;

    const updateBody = {
      freightZoneId: freightId,
      unloadingMethodId: this.quote?.shipping.unloadingMethodId,
    };

    this.orderService.updateOrderShipping(this.orderId, updateBody).subscribe({
      next: () => {
        this.isLoadingUpdateShipping = false;
        // Refrescar la cotización para obtener los datos actualizados
        if (this.orderId && this.quote?.shipping?.freightZoneId === null) {
          this.onQuote.emit(this.orderId);
        }
      },
      error: error => {
        console.error('Error updating shipping freight:', error);
        this.isLoadingUpdateShipping = false;
      },
    });
  }

  /**
   * Maneja la selección del método de descarga
   */
  protected onUnloadingMethodChange(methodId: number): void {
    if (!this.orderId || this.isLoadingUpdateShipping) {
      return;
    }

    this.selectedUnloadingMethod = methodId;
    this.updateDerivedState(); // Update calculated properties
    this.isLoadingUpdateShipping = true;

    const updateBody = {
      freightZoneId: this.quote?.shipping.freightZoneId,
      unloadingMethodId: methodId,
    };

    this.orderService.updateOrderShipping(this.orderId, updateBody).subscribe({
      next: () => {
        this.isLoadingUpdateShipping = false;
        // Refrescar la cotización para obtener los datos actualizados
        if (this.orderId && this.quote?.shipping?.unloadingMethodId === null) {
          this.onQuote.emit(this.orderId);
        }
      },
      error: error => {
        console.error('Error updating unloading method:', error);
        this.isLoadingUpdateShipping = false;
      },
    });
  }

  /**
   * Remueve la zona de flete seleccionada
   */
  protected onRemoveFreightZone(): void {
    this.showFreightZoneSelection = false;
    this.updateDerivedState(); // Update calculated properties
    if (
      this.orderId &&
      this.quote?.shipping?.freightZoneId !== null &&
      this.quote?.shipping?.freightZoneId !== this.selectedShippingFreight
    ) {
      this.onQuote.emit(this.orderId);
    }
  }

  /**
   * Remueve el método de descarga seleccionado
   */
  protected onRemoveUnloadingMethod(): void {
    this.showUnloadingMethodSelection = false;
    this.updateDerivedState(); // Update calculated properties
    if (
      this.orderId &&
      this.quote?.shipping?.unloadingMethodId !== null &&
      this.quote?.shipping?.unloadingMethodId !== this.selectedUnloadingMethod
    ) {
      this.onQuote.emit(this.orderId);
    }
  }

  /**
   * Obtiene el nombre de la zona de flete seleccionada
   */
  protected getSelectedFreightZoneName(): string {
    if (
      !this.quote?.shipping?.freightZoneId &&
      this.quote?.shipping?.freightZoneId === null
    ) {
      return '-';
    }

    if (this.quote?.shipping && this.quote?.shipping?.freightZoneId !== null) {
      return String(this.quote?.shipping?.freightZoneName);
    }
    return '-';
  }

  /**
   * Obtiene el nombre del método de descarga seleccionado
   */
  protected getSelectedUnloadingMethodName(): string {
    if (
      !this.quote?.shipping?.unloadingMethodId &&
      this.quote?.shipping.unloadingMethodId === null
    ) {
      return '-';
    }

    if (
      this.quote?.shipping &&
      this.quote?.shipping.unloadingMethodId !== null
    ) {
      return String(this.quote?.shipping?.unloadingMethodName);
    }

    return '-';
  }

  /**
   * Permite editar la zona de flete
   */
  protected onEditFreightZone(): void {
    this.showFreightZoneSelection = true;
    this.updateDerivedState(); // Update calculated properties
    this.selectedShippingFreight = this.quote?.shipping.freightZoneId || 0;

    // Cargar las opciones de freight zones
    const businessLineId = this.getCommonBusinessLineId();
    if (businessLineId) {
      this.orderService.getFreightZones(businessLineId).subscribe({
        next: zones => {
          this.shippingFreightOptions = zones;
        },
        error: () => {
          this.shippingFreightOptions = [];
        },
      });
    }
  }

  /**
   * Permite editar el método de descarga
   */
  protected onEditUnloadingMethod(): void {
    this.showUnloadingMethodSelection = true;
    this.updateDerivedState(); // Update calculated properties
    this.selectedUnloadingMethod = this.quote?.shipping.unloadingMethodId || 0;

    // Cargar las opciones de métodos de descarga
    this.orderService.getUnloadingMethods().subscribe({
      next: methods => {
        this.unloadingMethodOptions = methods;
      },
      error: () => {
        this.unloadingMethodOptions = [];
      },
    });
  }
}
